import "react-toastify/dist/ReactToastify.css";

import React, { useState } from "react";

import Button from "./Buttons";
import Column from "./Column";
import Icon from "./Icon";
import PropTypes from "prop-types";
import Row from "./Row";
import { Text, TextDescription } from "./Texts";
import _filter from "lodash/filter";
import _find from "lodash/find";
import _forEach from "lodash/forEach";
import _reduce from "lodash/reduce";
import colors from "../styles/colors";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const DropdownCheckBox = ({ items = [], selectedItems = [], maxSelection = 1, onApply, minWidth, position }) => {
  const [t] = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState(selectedItems);
  const [isOptionsVisible, setOptionsVisible] = useState();
  const selectedTitle = selectedItems.length
    ? _reduce(selectedItems, (result, value, index) => (index === 0 ? value.title : `${result}, ${value.title}`), "")
    : "-";

  const onConfirm = () => {
    if (selectedOptions.length === maxSelection) {
      onApply(selectedOptions);
      setOptionsVisible(!isOptionsVisible);
    }
  };
  const onDropdownButtonClicked = () => {
    //console.log("Dropdown clicked");
    setOptionsVisible(!isOptionsVisible);
    setSelectedOptions(selectedItems);
  };
  const onDropdownItemSelected = (item) => {
    if (!item.checked && selectedOptions.length >= maxSelection) {
      console.log("ALT", maxSelection);
      toast.warn(t("__toast_warning_limit_alternate_column", { count: maxSelection }), {
        toastId: "alt_column_warning",
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
      return;
    }

    item.checked = !item.checked;
    if (!item.checked) {
      setSelectedOptions(_filter(selectedOptions, (option) => option.value !== item.value));
    } else {
      setSelectedOptions([...selectedOptions, item]);
    }
  };
  const uncheckedItems = [];
  const checkedItems = selectedOptions.map((item, index) => {
    item.checked = true;
    return <DropdownItem key={index} item={item} onClick={onDropdownItemSelected}></DropdownItem>;
  });

  _forEach(items, (item, index) => {
    if (!_find(selectedOptions, { value: item.value })) {
      uncheckedItems.push(
        <DropdownItem key={index + selectedOptions.length} item={item} onClick={onDropdownItemSelected}></DropdownItem>,
      );
    }
  });

  return (
    <Container>
      <DropdownButton
        className="dropdown-button"
        alignItems="center"
        columnSpacing="8px"
        minWidth={minWidth}
        onClick={onDropdownButtonClicked}
      >
        <Column>
          <Text size="12px">{selectedTitle}</Text>
        </Column>
        <Column wrap>
          <Icon name="icon-down-dir" size="14px"></Icon>
        </Column>
      </DropdownButton>
      <ItemsContainer isVisible={isOptionsVisible} position={position} minWidth={minWidth}>
        <TextDescription align="left">{t("__selected_metrics")}</TextDescription>
        <div className="items">{checkedItems}</div>
        <TextDescription align="left">{t("__available_metrics")}</TextDescription>
        <div className="items">{uncheckedItems}</div>
        <div className="buttons">
          <Button onClick={onConfirm} disabled={selectedOptions.length < maxSelection} full>
            {t("__apply")}
          </Button>
          <Button onClick={onDropdownButtonClicked} type="link" full className="cancel-btn">
            {t("__cancel")}
          </Button>
        </div>
      </ItemsContainer>
      {isOptionsVisible && <Backdrop />}
    </Container>
  );
};

DropdownCheckBox.propTypes = {
  item: PropTypes.array,
  selectedItems: PropTypes.array,
  maxSelection: PropTypes.number,
  onApply: PropTypes.func,
  minWidth: PropTypes.string,
  position: PropTypes.object,
};

export default DropdownCheckBox;

const DropdownItem = ({ item, onClick }) => {
  return (
    <StyledItem onClick={() => onClick(item)}>
      <Icon name={item.checked ? "icon-ok" : ""} className={item.checked ? "-checked" : ""} />
      <Text size="12px">{item.title}</Text>
    </StyledItem>
  );
};

const Container = styled.div`
  position: relative;

  > *:first-child {
    cursor: pointer;
  }

  .custom-dropdown-button {
    display: flex;
    align-items: center;

    > * {
      min-width: 0;
    }
  }
`;

const DropdownButton = styled(Row).attrs((props) => ({
  minWidth: props.minWidth,
}))`
  border: 1px solid #f0f0f0;
  padding: 4px 8px 4px 16px;
  border-radius: 8px;
  box-sizing: border-box;
  min-width: ${(props) => props.minWidth};
`;

const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  minWidth: props.minWidth,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #f0f0f0;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  min-width: ${(props) => props.minWidth};
  padding: 16px 0;
  text-align: left;
  max-height: 170px;
  overflow: auto;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 64px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    min-width: unset;
    padding: 16px;
    max-height: unset;

    p {
      font-size: 14px;
    }
  }

  .items {
    max-height: calc(100vh - 150px);
  }

  .buttons {
    margin: 16px 0 0;
    button:last-child {
      margin: 0;
    }
  }
`;

const StyledItem = styled.div`
  padding: 8px 0;
  display: flex;
  flex-direction: row;
  align-items: center;

  i {
    border: 1px solid ${colors.subText};
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    margin: 0 16px 0 0;
    font-size: 16px;

    &.-checked {
      border-color: ${colors.primary};
      color: ${colors.white};
      background-color: ${colors.primary};
    }
  }

  @media screen and (max-width: 834px) {
    padding: 16px 0;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;
